const { merge, omit } = require("lodash");
const { AssetType } = require("../common/constants");
const { builtInFonts, getPrimaryFontId } = require("./fontConstants");
const icons = require("./icons.json");

const isObject = a => a instanceof Object;

// Firebase-style asset ids (e.g. "-Oabc..." or "teamId:-Oabc..."), not CSS keywords / face names.
const CUSTOM_FONT_ASSET_ID = /^(?:[\w-]+:)?-[\w-]+$/;

function addCustomFontAssetId(fontFamily, assetIds) {
    const primary = getPrimaryFontId(fontFamily);
    if (primary && !builtInFonts[primary] && CUSTOM_FONT_ASSET_ID.test(primary)) {
        assetIds.push({ id: primary });
    }
}

function isCatalogIconKey(iconKey) {
    if (!iconKey || typeof iconKey !== "string") {
        return false;
    }
    const bareKey = iconKey.replace(/^icon-(classic|chunky|doodle)-/, "");
    return icons[bareKey];
}

function parseHtmlDocument(html) {
    // Browser already has DOMParser (same as TextElement). Node (offline builder / admin)
    // does not — lazy-require linkedom only there so the client bundle doesn't need it
    // at module load for every assetUtils import.
    if (typeof DOMParser !== "undefined") {
        return new DOMParser().parseFromString(html, "text/html");
    }
    const { DOMParser: NodeDOMParser } = require("linkedom");
    return new NodeDOMParser().parseFromString(html, "text/html");
}

function addCustomFontAssetIdsFromHtml(html, assetIds) {
    // Same approach as TextElement font discovery: DOMParser + style.getPropertyValue.
    let document;
    try {
        document = parseHtmlDocument(html);
    } catch {
        return;
    }

    document.querySelectorAll("*").forEach(element => {
        if (element.style) {
            const fontFamily = element.style.getPropertyValue("font-family");
            if (fontFamily) {
                addCustomFontAssetId(fontFamily, assetIds);
            }
        }
    });
}

function migrateIconAssetId(id, theme, app) {
    if (/^icon-/.test(id)) {
        return id;
    } else {
        const style = (theme && theme.get("iconStyle")) || (app && app.currentTheme && app.currentTheme.get("iconStyle")) || "chunky";
        return `icon-${style}-${id}`;
    }
}

const contactInfoMap = {
    contactus: true,
    business_card: true
};

function getAssetIds(elementOrSlideModel, themeBackboneModel) {
    const assetIds = [];
    const isContactInfo = contactInfoMap[elementOrSlideModel?.template_id];
    const _getAssetIds = object => {
        if (!object) {
            return;
        }

        if (!isObject(object) && !Array.isArray(object)) {
            return;
        }

        if (isContactInfo) {
            let id;
            switch (object.type) {
                case "phone":
                    id = migrateIconAssetId("phone-call", themeBackboneModel);
                    assetIds.push({ id });
                    break;
                case "email":
                    id = migrateIconAssetId("symbol-at", themeBackboneModel);
                    assetIds.push({ id });
                    break;
                case "fax":
                    id = migrateIconAssetId("printer", themeBackboneModel);
                    assetIds.push({ id });
                    break;
                case "website":
                    id = migrateIconAssetId("internet", themeBackboneModel);
                    assetIds.push({ id });
                    break;
                case "social":
                    id = migrateIconAssetId(object.social_type, themeBackboneModel);
                    assetIds.push({ id });
                    break;
            }
        }

        if (object.content_type && object.content_type !== "map" && object.content_value) {
            if (object.content_type === "icon") {
                //migrate old asset ids
                const id = migrateIconAssetId(object.content_value, themeBackboneModel);
                assetIds.push({ id });
            } else {
                assetIds.push({ id: object.content_value });
            }
        }

        if (object.iconId) {
            const id = migrateIconAssetId(object.iconId, themeBackboneModel);
            assetIds.push({ id });
        }

        if (object.listIconId) {
            const id = migrateIconAssetId(object.listIconId, themeBackboneModel);
            assetIds.push({ id });
        }

        if (object.icon && typeof object.icon === "string" && isCatalogIconKey(object.icon)) {
            // This field is only for built-in icon names. Skip unknown names (no file to download).
            // Uploaded/custom icons use content_value instead and are handled above.
            const id = migrateIconAssetId(object.icon, themeBackboneModel);
            assetIds.push({ id });
        }

        if (object.assetMapCachingKey && object.assetId) {
            assetIds.push({ id: object.assetId });
        }

        if (object.mapImageAssetId && object.mapCachingKey) {
            assetIds.push({ id: object.mapImageAssetId });
        }

        if (object.audioAsset) {
            assetIds.push({ id: object.audioAsset });
        }

        if (object.videoAssetId) {
            assetIds.push({ id: object.videoAssetId });
        }

        if (object.svgAssetId) {
            assetIds.push({ id: object.svgAssetId });
        }

        // Per-block / HTML font-family overrides (format-bar custom fonts). Theme fonts are
        // collected separately via theme_font*FontId; without these, offline packaging misses
        // slide-level custom font assets.
        if (typeof object.fontFamily === "string") {
            addCustomFontAssetId(object.fontFamily, assetIds);
        }

        if (typeof object.html === "string") {
            addCustomFontAssetIdsFromHtml(object.html, assetIds);
        }

        Object.keys(object).map(key => {
            return _getAssetIds(object[key]);
        });
    };

    if (elementOrSlideModel?.metadata && elementOrSlideModel?.template_id && elementOrSlideModel?.states) {
        _getAssetIds(omit(elementOrSlideModel, ["metadata"]));
    } else {
        _getAssetIds(elementOrSlideModel);
    }

    return assetIds;
}

function getLogoPath(urlOrPath) {
    if (urlOrPath.indexOf("http") === 0) {
        const url = new URL(urlOrPath);
        if (url.host.includes("googleusercontent.com")) {
            // stored by google in their public bucket (g suite avatars)
            return url.href;
        } else if (url.host === "firebasestorage.googleapis.com") {
            // our legacy way of uploading logos and avatars in our private bucket
            // strip out the file name from tokens or other query params
            const pieces = url.pathname.split("/");
            return decodeURIComponent(pieces[pieces.length - 1]).replace("logos/", "");
        } else {
            // error kept to let us know if any other urls might be unaccounted for
            console.error(`getLogoPath() Unable to get logoPath for ${url.host}`);
        }
    } else {
        // new way of storing urls assumes we just store the file name
        return urlOrPath;
    }
}

// Theme background images used to be stored in logo storage (filename with extension).
// New uploads are assets and set content_type so we can tell them apart.
function isThemeBackgroundAsset(backgroundImage) {
    return Boolean(backgroundImage?.content_type && backgroundImage?.asset);
}

// Theme backgrounds can also be videos, which are played back muted and looping behind
// the slide content and fall back to their still frame in thumbnails and exports.
function isThemeBackgroundVideo(backgroundImage) {
    return backgroundImage?.content_type === AssetType.VIDEO || backgroundImage?.content_type === AssetType.STOCK_VIDEO;
}

function getThemeBackgroundStorageIds(backgroundImages) {
    const logoIds = [];
    const assetIds = [];

    Object.values(backgroundImages || {})
        .filter(backgroundImage => !!backgroundImage)
        .forEach(backgroundImage => {
            if (!backgroundImage.asset) {
                return;
            }
            if (isThemeBackgroundAsset(backgroundImage)) {
                assetIds.push(backgroundImage.asset);
            } else {
                logoIds.push(getLogoPath(backgroundImage.asset));
            }
        });

    return { logoIds, assetIds };
}

function mergeMediaElementModelDefaults(...models) {
    merge(
        ...models,
        {
            flipHorizontal: false,
            mediaOpacity: 1,
            aoiLeft: null,
            aoiRight: null,
            aoiTop: null,
            aoiBottom: null,
            scale: null,
            filter: null,
            filterBrightness: null,
            filterContrast: null,
            filterBlur: null,
        },
    );
}

function getElementModelUpdatesForAsset(asset) {
    const {
        type,
        id,
        size,
        assetProps,
        assetName,
        tags,
        fileType,
        hasAlpha,
    } = asset;

    const updates = {
        content_type: type,
        content_url: null,
        content_attribution: asset.attribution,
        assetProps: merge({}, assetProps, {
            originalSize: size && {
                width: asset.w,
                height: asset.h
            }
        }),
        assetName,
        tags,
        content_value: id,
    };

    // If we have a transparent image, remove the background color
    if (
        (
            type === AssetType.IMAGE ||
            type === AssetType.LOGO
        ) &&
        fileType !== "svg" &&
        hasAlpha
    ) {
        updates.cellColor = "none";
    }

    return updates;
}
function getSlideImageAssetIds(slideModel) {
    const ids = new Set();
    const add = id => {
        if (id) {
            ids.add(id);
        }
    };

    add(slideModel.slideImage?.assetId);
    add(slideModel.metadata?.slideImage?.assetId);
    add(slideModel.metadata?.generatedModel?.slideImage?.assetId);

    if (Array.isArray(slideModel.metadata?.generatedSlides)) {
        slideModel.metadata.generatedSlides.forEach(generatedSlide => {
            add(generatedSlide?.slideData?.slideImage?.assetId);
        });
    }

    if (Array.isArray(slideModel.metadata?.generatedIdeas)) {
        slideModel.metadata.generatedIdeas.forEach(generatedIdea => {
            add(generatedIdea?.slideModel?.slideImage?.assetId);
        });
    }

    return Array.from(ids);
}

module.exports = { getAssetIds, getSlideImageAssetIds, migrateIconAssetId, getLogoPath, isThemeBackgroundAsset, isThemeBackgroundVideo, getThemeBackgroundStorageIds, mergeMediaElementModelDefaults, getElementModelUpdatesForAsset };
